import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Lock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { deleteSavedReport, useSavedReports } from '../reportingApi';
import type { SavedReport } from '../reporting-types';

/**
 * The saved-report picker. A private report is its owner's alone — the list
 * endpoint already filters that server-side (`SavedReportsService.findAll`),
 * so what arrives here is exactly what this viewer may open.
 */
export function SavedReportsPanel({
  selectedId,
  onSelect,
  onNew,
  reloadToken,
}: {
  selectedId: string | null;
  onSelect: (report: SavedReport) => void;
  onNew: () => void;
  /** Bumped by the parent after a save, so a newly created report shows up. */
  reloadToken: number;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const { reports, loading, error, reload } = useSavedReports({ search: search || undefined, limit: 50 });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- a bumped token, not a value to read
  useEffect(reload, [reloadToken]);

  const remove = async (report: SavedReport, event: React.MouseEvent) => {
    event.stopPropagation();
    const ok = await confirm.confirm({
      title: t('reporting.saved.confirmDeleteTitle'),
      body: t('reporting.saved.confirmDeleteBody', { name: report.name }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(report.id);
    try {
      await deleteSavedReport(report.id);
      toast.success(t('reporting.saved.deleted', { name: report.name }));
      if (report.id === selectedId) onNew();
      reload();
    } catch {
      toast.error(t('reporting.errors.deleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('reporting.saved.title')}</h3>
        {can('reports.manage') && (
          <Button size="sm" variant="outline" onClick={onNew}>
            <Icon as={Plus} size="sm" aria-hidden />
            {t('reporting.saved.new')}
          </Button>
        )}
      </div>

      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('common.search')}
        aria-label={t('common.search')}
      />

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !reports.length ? (
          <EmptyState
            title={t('reporting.saved.empty')}
            hint={search ? t('reporting.saved.emptySearchHint') : t('reporting.saved.emptyHint')}
          />
        ) : (
          reports.map((report) => {
            const active = report.id === selectedId;
            return (
              <button
                key={report.id}
                type="button"
                onClick={() => onSelect(report)}
                className={
                  'flex w-full items-start gap-2 rounded-lg px-2 py-2 text-start text-sm ' +
                  (active ? 'bg-accent-soft text-accent-soft-fg' : 'hover:bg-hover')
                }
              >
                <Icon as={FileText} size="sm" className="mt-0.5 shrink-0 text-ink-muted" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{report.name}</span>
                    {!report.isShared && (
                      <Icon
                        as={Lock}
                        size="xs"
                        className="shrink-0 text-ink-muted"
                        aria-label={t('reporting.saved.private')}
                      />
                    )}
                  </span>
                  {report.description && (
                    <span className="block truncate text-xs text-ink-muted">{report.description}</span>
                  )}
                </span>
                {can('reports.manage') && (
                  <button
                    type="button"
                    onClick={(event) => remove(report, event)}
                    disabled={deleting === report.id}
                    aria-label={t('common.delete')}
                    className="shrink-0 rounded p-1 text-ink-muted hover:bg-hover hover:text-red-600 disabled:opacity-40"
                  >
                    <Icon as={Trash2} size="sm" />
                  </button>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
